import React, { useId, useRef, useState } from "react";
import classNames from "classnames";
import { TriangleSmallIcon } from "@icons";
import { Button } from "../Button/Button";
import styles from "./Pagination.module.scss";

interface PaginationProps {
    onPageChange(nextPage: number): void;
    currentPage: number;
    pageCount: number;
    className?: string;
    disabled?: boolean;
    size?: "md" | "lg";
    buttonsPosition?: "left" | "right";
    /** Scroll the window to the top on Previous and Next; off when the caller scrolls its own container. */
    shouldScrollWindow?: boolean;
}

/** Controls show with more than one page, or on a page past the last so the user can get back. */
export const hasPaginationControls = ({
    pageCount,
    currentPage,
}: Pick<PaginationProps, "pageCount" | "currentPage">) =>
    pageCount > 1 || currentPage > Math.max(pageCount, 1);

export const Pagination: React.FC<PaginationProps> = ({
    onPageChange,
    currentPage,
    pageCount,
    className,
    disabled,
    size: sizeProp,
    buttonsPosition = "left",
    shouldScrollWindow = true,
}) => {
    const size = sizeProp ?? "md";
    const inputRef = useRef<HTMLInputElement>(null);
    const totalId = useId();
    const [draft, setDraft] = useState<string | null>(null);

    const displayValue = draft ?? String(currentPage);
    const inputWidthCh = Math.max(displayValue.length, 1);
    const horizontalChromePx = size === "md" ? 22 : 26;

    const commitDraft = () => {
        if (draft === null) {
            return;
        }

        if (draft === "") {
            setDraft(null);
            return;
        }

        const safePage = Math.max(
            Math.min(Math.trunc(Number(draft)), pageCount),
            1,
        );
        setDraft(null);

        if (safePage !== currentPage) {
            onPageChange(safePage);
        }
    };

    const goToPage = (nextPage: number) => {
        onPageChange(nextPage);
        if (shouldScrollWindow) {
            window.scrollTo({ top: 0, behavior: "smooth" });
        }
    };

    const pagerInput = (
        <span className={styles.pager}>
            <input
                type="number"
                aria-label="Page"
                aria-describedby={totalId}
                placeholder="Enter page"
                value={displayValue}
                ref={inputRef}
                disabled={disabled}
                onFocus={() => setDraft(String(currentPage))}
                onChange={(event) => setDraft(event.target.value)}
                onBlur={commitDraft}
                onKeyDown={(event) => {
                    if (event.key === "Enter") {
                        inputRef.current?.blur();
                    }
                }}
                style={{
                    width: `calc(${inputWidthCh}ch + ${horizontalChromePx}px)`,
                }}
            />
            <span id={totalId} className={styles.totalContainer}>
                of {pageCount}
            </span>
        </span>
    );

    const pagerButtons = (
        <div className={styles.pagerButtonsContainer}>
            <Button
                variant="primary"
                size={size}
                className={styles.left}
                iconOnly
                aria-label="Previous page"
                onClick={() => goToPage(currentPage - 1)}
                disabled={!!disabled || currentPage <= 1}
            >
                <TriangleSmallIcon />
            </Button>
            <Button
                variant="primary"
                size={size}
                iconOnly
                className={styles.right}
                aria-label="Next page"
                onClick={() => goToPage(currentPage + 1)}
                disabled={!!disabled || currentPage >= pageCount}
            >
                <TriangleSmallIcon />
            </Button>
        </div>
    );

    return (
        <div
            className={classNames(styles.pagination, className, {
                [styles.medium]: size === "md",
            })}
        >
            {hasPaginationControls({ pageCount, currentPage }) && (
                <>
                    {buttonsPosition === "left" ? (
                        <>
                            {pagerButtons}
                            {pagerInput}
                        </>
                    ) : (
                        <>
                            {pagerInput}
                            {pagerButtons}
                        </>
                    )}
                </>
            )}
        </div>
    );
};
